// v4.3.0 — ReadAloudPrompt : « Souhaitez-vous que je vous lise les principales
// informations ? » après l'achat (maquette étape 3). TTS via /api/tts/speak.

import { useRef, useState } from "react";
import { Volume2, Pause, Loader2, RotateCcw } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function ReadAloudPrompt({ content }) {
  const [state, setState] = useState("ask"); // ask | loading | playing | done | dismissed | error
  const audioRef = useRef(null);
  const blobRef = useRef(null);
  const sourceRef = useRef(null); // 'audio' | 'speech'

  if (!content || state === "dismissed") return null;

  const bullets = Array.isArray(content.summary_bullets) ? content.summary_bullets : [];
  const speech = `Voici les principales informations de ${content.title}, édition du ${content.edition_date || "jour"}. ${bullets.join(". ")}.`;

  // Fallback voix navigateur (Web Speech API) si le TTS serveur est indisponible.
  function speakWithBrowser() {
    if (!("speechSynthesis" in window)) {
      setState("error");
      setTimeout(() => setState("ask"), 2500);
      return;
    }
    const u = new SpeechSynthesisUtterance(speech);
    u.lang = "fr-FR";
    u.rate = 0.95;
    u.onstart = () => setState("playing");
    u.onend = () => setState("done");
    u.onerror = () => setState("done");
    sourceRef.current = "speech";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }

  function stopAll() {
    if (audioRef.current) audioRef.current.pause();
    if (sourceRef.current === "speech" && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }

  async function play() {
    if (state === "playing") {
      stopAll();
      setState("done");
      return;
    }
    if (blobRef.current) {
      startAudio(blobRef.current);
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/tts/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: speech, normalize: true, userId: "content_read_aloud" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      blobRef.current = URL.createObjectURL(blob);
      startAudio(blobRef.current);
    } catch (_) {
      speakWithBrowser();
    }
  }

  function startAudio(url) {
    const audio = new Audio(url);
    audioRef.current = audio;
    sourceRef.current = "audio";
    audio.onended = () => setState("done");
    audio.onplay = () => setState("playing");
    audio.onerror = () => speakWithBrowser();
    audio.play().catch(() => speakWithBrowser());
  }

  return (
    <div data-testid="read-aloud-prompt" className="mt-4">
      <div className="rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3 text-sm text-white/85">
        Souhaitez-vous que je vous lise les principales informations ?
      </div>
      <div className="flex gap-2 mt-2.5">
        <button
          onClick={play}
          disabled={state === "loading"}
          data-testid="read-aloud-yes-btn"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-white/[0.07] border border-white/15 text-white text-sm font-medium hover:bg-white/[0.12] transition-all disabled:opacity-60"
        >
          {state === "loading" && <Loader2 size={15} className="animate-spin" />}
          {state === "playing" && <Pause size={15} />}
          {state === "done" && <RotateCcw size={15} />}
          {(state === "ask" || state === "error") && <Volume2 size={15} />}
          {state === "loading" ? "Préparation…"
            : state === "playing" ? "Pause"
            : state === "done" ? "Réécouter"
            : state === "error" ? "Réessayer"
            : "Oui, lis-moi"}
        </button>
        <button
          onClick={() => {
            stopAll();
            setState("dismissed");
          }}
          data-testid="read-aloud-no-btn"
          className="flex-1 py-2.5 rounded-2xl bg-white/[0.07] border border-white/15 text-white/70 text-sm font-medium hover:bg-white/[0.12] transition-all"
        >
          Non, merci
        </button>
      </div>
    </div>
  );
}
